import { MAX_NAME_LENGTH } from '~/consts';
import { PlayerNumber } from '~/modules/players/player-number';
import { normaliseProfileName, UserProfile } from '~/modules/user-profiles/profiles';

/**
 * What a slot with nobody on it holds: it keeps its "Player 2" fallback and its scores are recorded
 * under that. The value of an unset field rather than one of the options - the picker offers singers
 * only, and `Select` shows its placeholder for this the way an unset field does everywhere else.
 *
 * Taking a slot back off a profile is therefore not done here. It is the Profiles screen's own
 * "Assign to Player N" row, which toggles: pressed on a slot the profile already holds, it hands
 * that slot back (see `profile-page.tsx`).
 */
export const NO_SINGER = '';

/**
 * Opens the profile form. Mirrored to the phone like every other option: the form publishes a
 * keyboard layout of its own (the name as a `text` row, Save, Cancel) and offers the phone's own
 * camera for the picture, so there is nothing left about it a phone cannot finish. It was held back
 * while that was not true.
 */
export const NEW_SINGER = 'new-singer';

export interface SingerOption {
  value: string;
  label: string;
  disabled?: boolean;
  note?: string;
}

/** Every singer a slot can be handed to, in the order the profiles were created. */
export const singerOptions = (
  profiles: UserProfile[],
  heldBy: Partial<Record<string, PlayerNumber>> = {},
  slot?: PlayerNumber,
): SingerOption[] => [
  ...profiles.map((profile) => {
    const holder = heldBy[profile.id];

    // Locked while another slot sings as them: two slots under one name merge their scores.
    return holder === undefined || holder === slot
      ? { value: profile.id, label: profile.name }
      : { value: profile.id, label: profile.name, disabled: true, note: `Player ${holder + 1}` };
  }),
  { value: NEW_SINGER, label: '+ New singer…' },
];

export const profileHolders = (profileIds: Array<string | undefined>, slotsWithInput: PlayerNumber[]) => {
  const holders: Partial<Record<string, PlayerNumber>> = {};
  profileIds.forEach((id, number) => {
    if (id && slotsWithInput.includes(number as PlayerNumber)) holders[id] ??= number as PlayerNumber;
  });

  return holders;
};

/**
 * What the picker should be showing. A phone names its own slot and that name beats any assignment
 * (see `assign.ts`), so there the phone's name is the value and the picker does not open at all.
 */
export const currentSingerOption = (profile: UserProfile | undefined) => profile?.id ?? NO_SINGER;

export type NewSingerOutcome =
  | { kind: 'assign'; profile: UserProfile }
  | { kind: 'create'; name: string }
  | { kind: 'refuse' };

// A name typed on the phone's "+ New": an existing singer of that name is reused rather than
// duplicated, unless another slot is already singing as them.
export const resolveNewSinger = (
  value: string,
  profiles: UserProfile[],
  heldBy: Partial<Record<string, PlayerNumber>>,
  slot: PlayerNumber,
): NewSingerOutcome => {
  const name = value.trim().slice(0, MAX_NAME_LENGTH);
  if (!name) return { kind: 'refuse' };

  const existing = profiles.find((profile) => normaliseProfileName(profile.name) === normaliseProfileName(name));
  if (!existing) return { kind: 'create', name };

  return (heldBy[existing.id] ?? slot) === slot ? { kind: 'assign', profile: existing } : { kind: 'refuse' };
};
